import { createComponentInstance } from '@/utils/mount-component'
import { inBrowser } from '@/utils'
import VideoCallModal from './VideoCallModal.vue'

let instance

const DEFAULT_OPTIONS = {
  userInfo: {},
  autoCloseSeconds: 30,
}

let currentOptions = Object.assign({}, DEFAULT_OPTIONS)

function initInstance() {
  instance = createComponentInstance(VideoCallModal)
}

/**
 * Show video call modal
 * @param {Object} options - Modal options
 * @param {Object} options.userInfo - User information to display
 * @param {string} options.userInfo.name - User name
 * @param {string} options.userInfo.avatar - User avatar URL
 * @param {string} options.userInfo.coverImage - User cover image URL
 * @param {string} options.userInfo.flag - Country flag emoji
 * @param {string} options.userInfo.country - Country name
 * @param {number} options.autoCloseSeconds - Auto close timeout in seconds (default: 30)
 * @param {boolean} options.free - Whether the video call is free
 * @param {number} options.price - Video call price per minute
 * @param {string} options.cardType - Video call card type
 * @param {Function} options.onHangup - Callback when hang up button is clicked
 * @param {Function} options.onTimeout - Callback when timeout occurs
 * @param {Function} options.onClosed - Callback when modal is closed
 * @returns {Promise<'hangup' | 'timeout'>}
 */
export function showVideoCall(options) {
  if (!inBrowser) {
    return Promise.resolve(undefined)
  }

  return new Promise((resolve) => {
    if (!instance) {
      initInstance()
    }

    const mergedOptions = Object.assign({}, currentOptions, options)

    // Wrap callbacks to resolve/reject promise
    const originalOnHangup = mergedOptions.onHangup
    const originalOnTimeout = mergedOptions.onTimeout
    const originalOnClosed = mergedOptions.onClosed
    const originalOnClose = mergedOptions.onClose
    instance.open(
      Object.assign({}, mergedOptions, {
        onHangup: () => {
          originalOnHangup?.()
          instance.toggle(false)
          resolve('hangup')
        },
        onTimeout: () => {
          originalOnTimeout?.()
          instance.toggle(false)
          resolve('timeout')
        },
        onClosed: () => {
          originalOnClosed?.()
          if (instance?.unmount) {
            instance.unmount()
            instance = null
          }
        },
        onClose: () => {
          originalOnClose?.()
        },
      })
    )
  })
}

/**
 * Close the currently displayed video call modal
 */
export function closeVideoCall() {
  if (instance) {
    instance.toggle(false)
  }
}
